import { useState, useEffect, useCallback } from "react";

import useSupabase from "../../../../hooks/useSupabase";
import { useTournamentAuth } from "../../../../utils/authorization";
import { supabase } from "../../../../supabaseClient";
import { addCurrency, roundCurrency } from "../../../../utils/currencyUtils";
import { Card, AlertBox } from "../../../UI/DesignStandards";
import {
  sanitizeOperationError,
  logErrorForDebugging,
} from "../../../../utility/errorSanitization";

// Import the new focused components
import PrizeFundDistributionHeader from "./PrizeFundDistributionHeader";
import PrizeFundDistributionList from "./PrizeFundDistributionList";
import PrizeFundDistributionActions from "./PrizeFundDistributionActions";
import { useTournamentId } from "../../../../context/TournamentRouteContext";

const AssignAdditionalPrizeFund = () => {
  const tournamentId = useTournamentId();
  const [editMode, setEditMode] = useState(false);
  const [prizeFundData, setPrizeFundData] = useState([]);
  const [combinedEventData, setCombinedEventData] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPayoutFinalized, setIsPayoutFinalized] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // TODO: Remove this feature flag after RLS testing is complete

  const { canManage } = useTournamentAuth(tournamentId);
  const { isLoading, error, fetchData, upsertData } = useSupabase();

  const fetchEventAndPrizeFundData = useCallback(
    async (tournamentID, isRealTimeUpdate = false) => {
      try {
        if (isRealTimeUpdate) {
          setIsRefreshing(true);
        }

        // Fetch prize fund data
        const prizeResponse = await fetchData("tournamentAddedPrizeFund", {
          tournamentID: tournamentID,
        });
        setPrizeFundData(prizeResponse || []);

        // Fetch event data (exclude deleted events)
        const eventResponse = await fetchData("tournamentEvents", {
          tournamentID: tournamentID,
          isDeleted: false,
        });

        if (eventResponse && eventResponse.length > 0) {
          // Get all event IDs
          const eventIds = eventResponse.map((event) => event.id);

          // Fetch all eventAddedPrizeFund entries for these events
          const prizeFunds = await fetchData("eventAddedPrizeFund");
          const allPrizeFunds = prizeFunds
            ? prizeFunds.filter((fund) => eventIds.includes(fund.eventsID))
            : [];

          // Create a map of eventID to prize fund amount
          const prizeFundMap = allPrizeFunds.reduce((acc, fund) => {
            acc[fund.eventsID] = fund.amount;
            return acc;
          }, {});

          // Combine event data with prize fund amounts and sort by primary events first
          // Filter out deleted events as an additional safety measure
          const combinedData = eventResponse
            .filter((event) => !event.isDeleted)
            .map((event) => ({
              ...event,
              prizeAmount: prizeFundMap[event.id] || 0,
            }))
            .sort((a, b) => {
              if (a.isPrimary && !b.isPrimary) return -1;
              if (!a.isPrimary && b.isPrimary) return 1;
              return 0;
            });

          setCombinedEventData(combinedData);
        } else {
          setCombinedEventData([]);
        }
      } catch (err) {
        // Error handling is silent for data fetching
      } finally {
        if (isRealTimeUpdate) {
          setIsRefreshing(false);
        }
      }
    },
    [fetchData]
  );

  const fetchPayoutStatus = async () => {
    try {
      const { data: verificationStates, error } = await supabase
        .from("verificationStates")
        .select("*")
        .eq("tournamentId", tournamentId)
        .eq("entityType", "payouts")
        .is("entityId", null);

      if (error) throw error;

      // Handle case where no verification states exist yet
      const payoutVerification = verificationStates?.[0];
      setIsPayoutFinalized(payoutVerification?.isVerified || false);
    } catch (err) {
      // Error handling is silent for payout status, default to false
      setIsPayoutFinalized(false);
    }
  };

  useEffect(() => {
    if (!tournamentId) return;

    fetchEventAndPrizeFundData(tournamentId);
    fetchPayoutStatus();

    // Set up real-time subscriptions
    const tournamentEventsChannel = supabase
      .channel("tournament_events_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "tournamentEvents",
        },
        (payload) => {
          if (payload.eventType === "DELETE") {
            fetchEventAndPrizeFundData(tournamentId, true);
          } else {
            const event = payload.new;
            if (event && event.tournamentID === tournamentId) {
              fetchEventAndPrizeFundData(tournamentId, true);
            }
          }
        }
      )
      .subscribe();

    const eventPrizeFundChannel = supabase
      .channel("event_prize_fund_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "eventAddedPrizeFund",
        },
        (payload) => {
          fetchEventAndPrizeFundData(tournamentId, true);
        }
      )
      .subscribe();

    const tournamentPrizeFundChannel = supabase
      .channel("tournament_prize_fund_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "tournamentAddedPrizeFund",
        },
        (payload) => {
          if (payload.eventType === "DELETE") {
            fetchEventAndPrizeFundData(tournamentId, true);
          } else {
            const prizeFund = payload.new;
            if (prizeFund && prizeFund.tournamentID === tournamentId) {
              fetchEventAndPrizeFundData(tournamentId, true);
            }
          }
        }
      )
      .subscribe();

    // Set up real-time subscription for verification changes (payout locking)
    const verificationChannel = supabase
      .channel("verification_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "verificationStates",
        },
        (payload) => {
          // TODO: Remove this feature flag after RLS testing is complete

          if (
            payload.new &&
            payload.new.tournamentId === tournamentId &&
            payload.new.entityType === "payouts"
          ) {
            setIsPayoutFinalized(payload.new.isVerified || false);
          }
        }
      )
      .subscribe();

    return () => {
      if (tournamentEventsChannel) {
        tournamentEventsChannel.unsubscribe();
      }
      if (eventPrizeFundChannel) {
        eventPrizeFundChannel.unsubscribe();
      }
      if (tournamentPrizeFundChannel) {
        tournamentPrizeFundChannel.unsubscribe();
      }
      if (verificationChannel) {
        verificationChannel.unsubscribe();
      }
    };
  }, [tournamentId, fetchEventAndPrizeFundData]);

  const toggleEditMode = () => {
    setEditMode((prev) => !prev);
    setValidationErrors({});
  };

  const validateAmounts = (updatedData) => {
    const errors = {};

    // Calculate total assigned (treating empty/undefined as 0)
    const totalAssigned = updatedData.reduce(
      (sum, item) => sum + (parseFloat(item.prizeAmount) || 0),
      0
    );

    const totalAvailable = prizeFundData.reduce(
      (sum, prizeFund) => sum + (parseFloat(prizeFund.amount) || 0),
      0
    );

    // Check if total assigned exceeds available
    if (totalAssigned > totalAvailable) {
      errors.total = `Cannot assign more than available prize fund ($${totalAvailable.toFixed(
        2
      )}). Current total: $${totalAssigned.toFixed(2)}`;
    }

    // Validate individual amounts
    updatedData.forEach((item) => {
      const amount = parseFloat(item.prizeAmount);
      if (isNaN(amount) || amount < 0) {
        errors[item.id] = "Amount must be a positive number or zero";
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    try {
      setErrorMessage("");
      setIsSubmitting(true);

      // Validate amounts before proceeding
      if (!validateAmounts(combinedEventData)) {
        setIsSubmitting(false);
        return; // Validation errors are already set
      }

      const totalAssigned = combinedEventData.reduce(
        (sum, event) => sum + (parseFloat(event.prizeAmount) || 0),
        0
      );

      if (totalAssigned > totalPrizeFund) {
        setErrorMessage(
          "Total assigned amount cannot exceed available prize fund amount."
        );
        setIsSubmitting(false);
        return;
      }

      // Separate events with positive amounts and zero amounts
      const eventsWithPositiveAmounts = combinedEventData.filter(
        (event) => parseFloat(event.prizeAmount) > 0
      );

      const eventsWithZeroAmounts = combinedEventData.filter(
        (event) => parseFloat(event.prizeAmount) === 0 || !event.prizeAmount
      );

      // Prepare data for upsert (positive amounts)
      const positiveAmountData = eventsWithPositiveAmounts.map(
        ({ prizeAmount, id }) => ({
          amount: parseFloat(prizeAmount) || 0,
          eventsID: id,
        })
      );

      // First, delete any existing entries for events with zero amounts
      if (eventsWithZeroAmounts.length > 0) {
        const zeroAmountEventIds = eventsWithZeroAmounts.map(
          (event) => event.id
        );

        // Delete existing entries for these events
        const deleteResult = await supabase
          .from("eventAddedPrizeFund")
          .delete()
          .in("eventsID", zeroAmountEventIds);

        if (deleteResult.error) {
          const sanitizedMessage = sanitizeOperationError.delete(
            deleteResult.error,
            "prize fund distribution",
            isPayoutFinalized
          );
          logErrorForDebugging(
            deleteResult.error,
            "delete zero amount prize fund entries",
            sanitizedMessage
          );
          setErrorMessage(sanitizedMessage);
          setIsSubmitting(false);
          return;
        }
      }

      // Then, upsert the positive amounts
      if (positiveAmountData.length > 0) {
        const result = await upsertData(
          "eventAddedPrizeFund",
          positiveAmountData,
          {
            eventsID: positiveAmountData.map((data) => data.eventsID),
          }
        );

        if (result.error) {
          const sanitizedMessage = sanitizeOperationError.save(
            result.error,
            "prize fund distribution",
            isPayoutFinalized
          );
          logErrorForDebugging(
            result.error,
            "save prize fund distribution",
            sanitizedMessage
          );
          setErrorMessage(sanitizedMessage);
          setIsSubmitting(false);
          return;
        }
      }

      fetchEventAndPrizeFundData(tournamentId);
      setEditMode(false);
    } catch (err) {
      const sanitizedMessage = sanitizeOperationError.save(
        err,
        "prize fund distribution",
        isPayoutFinalized
      );
      logErrorForDebugging(
        err,
        "save prize fund distribution",
        sanitizedMessage
      );
      setErrorMessage(sanitizedMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalPrizeFund = prizeFundData.reduce(
    (sum, prizeFund) => addCurrency(sum, parseFloat(prizeFund.amount) || 0),
    0
  );

  const totalAssigned = combinedEventData.reduce(
    (sum, event) => addCurrency(sum, parseFloat(event.prizeAmount) || 0),
    0
  );

  const handlePrizeAmountChange = (id, value) => {
    // Convert empty string to 0, otherwise parse the value
    const numericValue = value === "" ? 0 : parseFloat(value) || 0;

    const newData = combinedEventData.map((event) =>
      event.id === id
        ? { ...event, prizeAmount: numericValue === 0 ? 0 : value }
        : event
    );
    setCombinedEventData(newData);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setValidationErrors({});
    // Refresh data to reset any unsaved changes
    fetchEventAndPrizeFundData(tournamentId);
  };

  return (
    <Card>
      <PrizeFundDistributionHeader
        totalPrizeFund={totalPrizeFund}
        totalAssigned={totalAssigned}
        isRefreshing={isRefreshing}
        isPayoutFinalized={isPayoutFinalized}
        canManage={canManage}
        editMode={editMode}
        isSubmitting={isSubmitting}
        onEditDistribution={toggleEditMode}
      />

      {errorMessage && (
        <div className="mb-4">
          <AlertBox
            type="error"
            title="There was a problem"
            message={errorMessage}
            onClose={() => setErrorMessage("")}
          />
        </div>
      )}

      {validationErrors.total && (
        <AlertBox type="error" message={validationErrors.total} />
      )}

      <PrizeFundDistributionList
        isLoading={isLoading}
        combinedEventData={combinedEventData}
        isRefreshing={isRefreshing}
        isSubmitting={isSubmitting}
        editMode={editMode}
        validationErrors={validationErrors}
        isPayoutFinalized={isPayoutFinalized}
        onPrizeAmountChange={handlePrizeAmountChange}
      />

      <PrizeFundDistributionActions
        editMode={editMode}
        canManage={canManage}
        isPayoutFinalized={isPayoutFinalized}
        isSubmitting={isSubmitting}
        onCancel={handleCancelEdit}
        onSave={handleSave}
      />
    </Card>
  );
};

export default AssignAdditionalPrizeFund;
